import { get } from '@shared/adapters/lodash'
import { formatKey } from '@shared/utils'
import type {
  Transaction,
  TransactionStatus,
  TransactionType,
} from '../../transactions/types/transactionTypes'
import type { FiatWalletRecentTransactionWireDto } from './fiatWalletContracts'
import type { AddFundsResult, FiatWallet, SlicealAccount } from '../types/fiatWalletTypes'

const REASON_TO_TYPE: Record<string, TransactionType> = {
  deposit: 'deposit',
  refund: 'deposit',
  investment: 'investment',
  investment_payment: 'investment',
  rental: 'rental',
  rental_income: 'rental_income',
  withdrawal: 'withdrawal',
  token: 'token_received',
  token_received: 'token_received',
}

function mapTransactionType(reason: string): TransactionType {
  return REASON_TO_TYPE[reason.toLowerCase()] ?? 'deposit'
}

const STATUS_MAP: Record<string, TransactionStatus> = {
  completed: 'completed',
  pending: 'pending',
  processing: 'processing',
  failed: 'failed',
  reversed: 'reversed',
}

function mapTransactionStatus(status: string): TransactionStatus {
  return STATUS_MAP[status.toLowerCase()] ?? 'pending'
}

import { ONE_MINUTE_MS } from '@shared/utils'

const MINUTES_PER_HOUR = 60

function formatDate(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatTime(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
}

function isSameCalendarDay(now: Date, other: Date): boolean {
  return (
    now.getFullYear() === other.getFullYear() &&
    now.getMonth() === other.getMonth() &&
    now.getDate() === other.getDate()
  )
}

function formatLastUpdated(iso: string): string {
  if (!iso) return ''
  const now = new Date()
  const updated = new Date(iso)
  const diffMins = Math.floor((now.getTime() - updated.getTime()) / ONE_MINUTE_MS)

  if (diffMins < 1) return 'Just now'
  if (diffMins < MINUTES_PER_HOUR) return `${diffMins} min ago`

  const time = formatTime(iso)
  return isSameCalendarDay(now, updated) ? `Today, ${time}` : `${formatDate(iso)}, ${time}`
}

function formatAmount(amount: string, currencyCode: string, isIncoming: boolean): string {
  const num = parseFloat(amount)
  if (isNaN(num)) return `${currencyCode} 0.00`
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return isIncoming ? `+${currencyCode} ${formatted}` : `${currencyCode} ${formatted}`
}

export function formatBalance(amount: string): string {
  const num = parseFloat(amount)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Wire row arrives camelCase from the backend; pick the camelCase keys first
// and fall through to the legacy snake_case keys so this stays resilient to
// pre-camelCase fixtures and any not-yet-migrated callers (mocks, tests).
function parseRecentTransaction(raw: FiatWalletRecentTransactionWireDto): Transaction {
  const transactionType = get(raw, 'transactionType') ?? get(raw, 'transaction_type') ?? 'credit'
  const isIncoming = (transactionType as string) === 'credit'
  const reason: string = get(raw, 'transactionReason') ?? get(raw, 'transaction_reason') ?? ''
  const createdAt: string = get(raw, 'createdAt') ?? get(raw, 'created_at') ?? ''
  const currency = get(raw, 'currencyCode') ?? get(raw, 'currency_code') ?? 'AED'
  const displayTitle = get(raw, 'displayTitle') ?? get(raw, 'display_title') ?? ''

  return {
    id: get(raw, 'id') ?? '',
    type: mapTransactionType(reason),
    title: displayTitle,
    category: formatKey(reason),
    date: formatDate(createdAt),
    time: formatTime(createdAt),
    referenceId: get(raw, 'referenceId') ?? get(raw, 'reference_id') ?? null,
    amount: formatAmount(get(raw, 'amount') ?? '0', currency as string, isIncoming),
    incoming: isIncoming,
    status: mapTransactionStatus(get(raw, 'status') ?? ''),
  }
}

export const parseFiatWallet = (resp: unknown): FiatWallet => {
  const rawTransactions: FiatWalletRecentTransactionWireDto[] =
    get(resp, 'data.recentTransactions') ?? get(resp, 'data.recent_transactions') ?? []

  return {
    id: get(resp, 'data.id') ?? '',
    availableBalance:
      get(resp, 'data.availableBalance') ?? get(resp, 'data.available_balance') ?? '0.00',
    lockedBalance: get(resp, 'data.lockedBalance') ?? get(resp, 'data.locked_balance') ?? '0.00',
    totalBalance: get(resp, 'data.totalBalance') ?? get(resp, 'data.total_balance') ?? '0.00',
    currencyCode: get(resp, 'data.currencyCode') ?? get(resp, 'data.currency_code') ?? 'AED',
    lastUpdatedAt: get(resp, 'data.lastUpdatedAt') ?? get(resp, 'data.last_updated_at') ?? '',
    recentTransactions: rawTransactions.map(parseRecentTransaction),
  }
}

export interface FiatWalletDisplay {
  currency: string
  /** User-facing spendable balance, formatted with thousands separators (e.g. "12,000.00"). */
  balance: string
  availableBalance: string
  lockedBalance: string
  totalBalance: string
  lastUpdated: string
  transactions: Transaction[]
}

export function toFiatWalletDisplay(data: FiatWallet): FiatWalletDisplay {
  return {
    currency: data.currencyCode,
    balance: formatBalance(data.availableBalance),
    availableBalance: formatBalance(data.availableBalance),
    lockedBalance: formatBalance(data.lockedBalance),
    totalBalance: formatBalance(data.totalBalance),
    lastUpdated: formatLastUpdated(data.lastUpdatedAt),
    transactions: data.recentTransactions,
  }
}

export const parseSlicealAccount = (resp: unknown): SlicealAccount => ({
  accountName: get(resp, 'data.accountName') ?? get(resp, 'data.account_name') ?? '',
  bankName: get(resp, 'data.bankName') ?? get(resp, 'data.bank_name') ?? '',
  iban: get(resp, 'data.iban') ?? '',
  swiftBic: get(resp, 'data.swiftBic') ?? get(resp, 'data.swift_bic') ?? '',
})

export const parseAddFunds = (resp: unknown): AddFundsResult => ({
  id: get(resp, 'data.id') ?? '',
  amount: get(resp, 'data.amount') ?? '0.00',
  currencyCode: get(resp, 'data.currencyCode') ?? get(resp, 'data.currency_code') ?? 'AED',
  status: get(resp, 'data.status') ?? '',
  createdAt: get(resp, 'data.createdAt') ?? get(resp, 'data.created_at') ?? '',
})
